import React, { useState, useEffect, useRef, useCallback } from 'react';
import { notificationService } from '../services/api';
import NotificationsPanel from './NotificationsPanel';
import './NotificationsPanel.css';
import './UserHeader.css';

function UserHeader({ user, onLogout, onOpenProfile, onOpenSettings, onOpenSupport }) {
  // TD-followup-prompt-unsaved-changes-spa (Sessão 21): delega 100% pro
  // App.jsx via onLogout (que faz authService.logout() + setUser(null) +
  // o guard de unsaved changes). Antes, UserHeader chamava
  // authService.logout() ANTES de onLogout() — token era limpo mesmo se
  // o user cancelasse o prompt de "alterações não salvas". Bug latente
  // exposto pela TD do guard SPA.
  const handleLogout = () => {
    onLogout();
  };

  // TD-followup-edicao-perfil-usuario (Sessão 23): clique no
  // avatar/nome abre modal "Meu Perfil" (edição de nome + troca de senha).
  // Callback fica no App.jsx que controla o modal + refresh de user.
  const handleOpenProfile = () => {
    if (typeof onOpenProfile === 'function') onOpenProfile();
  };

  const getRoleBadgeText = (role) => {
    const badges = {
      admin: 'ADMIN',
      operador: 'OPERADOR',
      auditor: 'AUDITOR'
    };
    return badges[role] || role.toUpperCase();
  };

  // === Central de Notificações (backend#306) ===
  // O sino (envelope `mail`, livre desde #235) abre o painel. O header detém o
  // estado da contagem/lista pra manter o badge vivo; o backend já filtra a
  // visibilidade e devolve `nao_lidas` + `notificacoes[].lida` (não
  // reimplementamos a regra de visibilidade).
  const [notifOpen, setNotifOpen] = useState(false);
  const [naoLidas, setNaoLidas] = useState(0);
  const [notificacoes, setNotificacoes] = useState([]);
  const notifRef = useRef(null);

  const carregarNotificacoes = useCallback(async () => {
    try {
      const data = await notificationService.listar();
      setNaoLidas(data?.nao_lidas || 0);
      setNotificacoes(Array.isArray(data?.notificacoes) ? data.notificacoes : []);
    } catch (err) {
      // Silencioso: o sino não deve quebrar o header se o endpoint falhar.
    }
  }, []);

  // Carrega ao montar.
  useEffect(() => {
    carregarNotificacoes();
  }, [carregarNotificacoes]);

  // Fecha o painel ao clicar fora ou apertar Esc.
  useEffect(() => {
    if (!notifOpen) return undefined;
    const onClickFora = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setNotifOpen(false);
      }
    };
    const onEsc = (e) => {
      if (e.key === 'Escape') setNotifOpen(false);
    };
    document.addEventListener('mousedown', onClickFora);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClickFora);
      document.removeEventListener('keydown', onEsc);
    };
  }, [notifOpen]);

  const toggleNotif = () => {
    const next = !notifOpen;
    setNotifOpen(next);
    if (next) carregarNotificacoes(); // recarrega ao abrir
  };

  return (
    <header className="qaa-topnavbar">
      <div className="topnavbar-left-section">
        <div className="logo-brand-group">
          <img
            src="/qaasa-icon-dark.png"
            alt="Qaa.sa Logo"
            className="header-logo-img"
          />
          <div className="brand-text-group">
            <span className="brand-name">Qaa.sa</span>
            <span className="brand-tagline">Quality as a service, powered by AI</span>
          </div>
        </div>
      </div>

      <div className="topnavbar-right-section">
        {/* Central de Notificações (backend#306): sino (envelope `mail`, livre
            desde #235) + badge de não-lidas. Disponível pra TODO usuário logado
            (qualquer role), em toda tela. Carrega ao montar e ao abrir. */}
        <div className="action-icons-group notif-bell-wrap" ref={notifRef}>
          <button
            className="action-icon-btn notif-bell-btn"
            title="Notificações"
            aria-label="Notificações"
            aria-haspopup="dialog"
            aria-expanded={notifOpen}
            onClick={toggleNotif}
          >
            <span className="material-symbols-outlined">mail</span>
            {naoLidas > 0 && (
              <span className="notif-badge-count">{naoLidas > 99 ? '99+' : naoLidas}</span>
            )}
          </button>
          {notifOpen && (
            <div className="notif-dropdown">
              <NotificationsPanel
                notificacoes={notificacoes}
                user={user}
                onClose={() => setNotifOpen(false)}
                onChanged={carregarNotificacoes}
              />
            </div>
          )}
        </div>

        {/* #260: canal de suporte in-app. Disponível pra TODO usuário logado
            (qualquer role), em toda tela — abre o modal "Falar com o suporte". */}
        <div className="action-icons-group">
          <button
            className="action-icon-btn"
            title="Falar com o suporte"
            aria-label="Falar com o suporte"
            onClick={onOpenSupport}
          >
            {/* nota: o envelope (mail) agora é o sino de notificações; suporte usa support_agent */}
            <span className="material-symbols-outlined">support_agent</span>
          </button>
        </div>

        {/* #124 (#129): engrenagem abre a tela "Conta" via onOpenSettings.
            Gated a admin — espelha o item "Conta" da sidebar (que é admin-only);
            evita expor configurações da empresa a operador/auditor. */}
        {user.role === 'admin' && (
          <div className="action-icons-group">
            <button className="action-icon-btn" title="Configurações" onClick={onOpenSettings}>
              <span className="material-symbols-outlined">settings</span>
            </button>
          </div>
        )}

        <button
          className="user-profile-cluster"
          onClick={handleOpenProfile}
          title="Meu Perfil"
          type="button"
        >
          <div className="user-info-text">
            <div className="user-name-badge-row">
              <span className="user-display-name">{user.nome}</span>
              <span className="user-role-badge">{getRoleBadgeText(user.role)}</span>
            </div>
            <p className="user-display-email">{user.email}</p>
          </div>
          <div className="user-avatar-wrapper">
            <img
              src={`https://ui-avatars.com/api/?name=${encodeURIComponent(user.nome)}&background=00855d&color=fff&bold=true`}
              alt="User avatar"
              className="user-avatar-img"
            />
          </div>
        </button>

        <button className="logout-action-btn" onClick={handleLogout}>
          <span className="material-symbols-outlined logout-icon">logout</span>
          <span className="logout-text">Sair</span>
        </button>
      </div>
    </header>
  );
}

export default UserHeader;
